import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Spin, Alert, Typography, List, Form, Input, Button, Select, InputNumber, Space, message, Divider, Dropdown, Menu } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined, ArrowLeftOutlined, DownloadOutlined, CreditCardOutlined, DownOutlined } from '@ant-design/icons';
import ordersService, { Order, getOrderById, updateOrder, updatePaymentStatus } from '../../services/ordersService';
import { useAuth } from '../../contexts/AuthContext';
import PaymentLinkGenerator from '../../components/Payment/PaymentLinkGeneratorAntd';
import api from '../../services/api';

const { Title, Text } = Typography;

// Helper pour formater les modes de paiement
const paymentMethodLabels: Record<string, string> = {
  'cash': 'Espèces',
  'mobile_money': 'Mobile Money',
  'bank_transfer': 'Virement bancaire',
  'card': 'Carte bancaire'
};

const getPaymentMethodLabel = (method: string | undefined): string => {
  if (!method) return 'Non spécifié';
  return paymentMethodLabels[method] || method;
};

// Helper pour formater les statuts
const statusLabels: Record<string, string> = {
  'pending': 'En attente',
  'processing': 'En cours',
  'completed': 'Terminée',
  'cancelled': 'Annulée'
};

const getStatusLabel = (status: string | undefined): string => {
  if (!status) return 'Non spécifié';
  return statusLabels[status.toLowerCase()] || status;
};

// Helper pour formater les statuts de paiement
const paymentStatusLabels: Record<string, string> = {
  'pending': 'En attente',
  'paid': 'Payée',
  'failed': 'Échouée',
  'refunded': 'Remboursée'
};

const getPaymentStatusLabel = (status: string | undefined): string => {
  if (!status) return 'Non spécifié';
  return paymentStatusLabels[status.toLowerCase()] || status;
};

const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm] = Form.useForm();
  const [editItems, setEditItems] = useState<any[]>([]);


  useEffect(() => {
    if (!id || id === 'nouveau') return;
    // Ne charger que si l'id est un nombre
    if (isNaN(Number(id))) return;
    setLoading(true);
    getOrderById(id)
      .then((data) => {
        if (data) {
          console.log('Données de commande reçues:', data);
          console.log('createdAt:', data.createdAt);
          setOrder(data);
          // Pré-remplir le formulaire d'édition
          editForm.setFieldsValue({
            status: data.status,
            shipping_address: data.shippingAddress,
            payment_method: data.paymentMethod,
            notes: data.notes
          });
          setError(null);
        } else {
          setError("Commande introuvable.");
        }
      })
      .catch(() => {
        setError("Impossible de charger la commande.");
      })
      .finally(() => setLoading(false));
  }, [id, editForm]);



  // --- Formulaire de création de commande ---
  const [form] = Form.useForm();
  const [products, setProducts] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [addingItem, setAddingItem] = useState<{productId?: number, quantity?: number}>({});
  const [loadingForm, setLoadingForm] = useState(false);

  useEffect(() => {
    if (id !== 'nouveau') return;
    // Charger produits et clients pour le formulaire de création
    api.get('/products')
      .then(r => setProducts(r.data?.donnees?.produits || r.data?.data || []))
      .catch(() => message.error('Erreur lors du chargement des produits'));
    api.get('/users', { params: { role: 'customer' } })
      .then(r => setCustomers(r.data?.data?.users || []))
      .catch(() => message.error('Erreur lors du chargement des clients'));
  }, [id]);

  // Charger les produits pour le mode édition aussi
  useEffect(() => {
    if (id === 'nouveau' || !id) return;
    api.get('/products')
      .then(r => setProducts(r.data?.donnees?.produits || r.data?.data || []))
      .catch(() => {});
  }, [id]);

  const handleAddItem = () => {
    const prod = products.find((p: any) => p.id === addingItem.productId);
    if (!prod || !addingItem.quantity || addingItem.quantity < 1) return;
    setItems([...items, {
      productId: prod.id,
      productName: prod.nom,
      quantity: addingItem.quantity,
      unitPrice: prod.prix,
      totalPrice: prod.prix * addingItem.quantity
    }]);
    setAddingItem({});
  };

  const handleRemoveItem = (idx: number) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const handleCreateOrder = async (values: any) => {
    if (items.length === 0) {
      message.error('Ajoutez au moins un produit');
      return;
    }
    setLoadingForm(true);
    try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch('/api/orders', {
        method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
        body: JSON.stringify({
          customer_id: values.customer_id,
          shipping_address: values.shipping_address,
          payment_method: values.payment_method,
          notes: values.notes,
          items: items.map(i => ({ product_id: i.productId, quantity: i.quantity, unit_price: i.unitPrice }))
        })
      });
      if (response.ok) {
        message.success('Commande créée !');
        form.resetFields();
        setItems([]);
        // Rediriger vers la liste des commandes
        navigate('/commandes');
      } else {
        message.error('Erreur lors de la création');
      }
    } catch (e) {
      message.error('Erreur lors de la création');
    } finally {
      setLoadingForm(false);
    }
  };

  if (id === 'nouveau') {
    const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager';
    
    return (
      <Card>
        <Title level={3}>Nouvelle commande</Title>
        <Form layout="vertical" form={form} onFinish={handleCreateOrder}>
          {isAdmin && (
            <Form.Item label="Client" name="customer_id" rules={[{ required: false }]}> 
              <Select showSearch optionFilterProp="children" placeholder="Choisir un client (optionnel - par défaut vous-même)">
                {customers.map((c: any) => (
                  <Select.Option key={c.id} value={c.id}>{c.first_name || ''} {c.last_name || ''} ({c.email})</Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item label="Adresse de livraison" name="shipping_address">
            <Input />
          </Form.Item>
          <Form.Item label="Mode de paiement" name="payment_method" rules={[{ required: true, message: 'Veuillez sélectionner un mode de paiement' }]}>
            <Select placeholder="Sélectionner un mode de paiement">
              <Select.Option value="cash">Espèces</Select.Option>
              <Select.Option value="mobile_money">Mobile Money</Select.Option>
              <Select.Option value="bank_transfer">Virement bancaire</Select.Option>
              <Select.Option value="card">Carte bancaire</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label="Notes" name="notes">
            <Input.TextArea />
          </Form.Item>
          <Form.Item label="Articles">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                <Select
                  style={{ minWidth: 180 }}
                  placeholder="Produit"
                  value={addingItem.productId}
                  onChange={v => setAddingItem(a => ({ ...a, productId: v }))}
                >
                  {products.map((p: any) => (
                    <Select.Option key={p.id} value={p.id}>{p.nom}</Select.Option>
                  ))}
                </Select>
                <InputNumber
                  min={1}
                  placeholder="Quantité"
                  value={addingItem.quantity}
                  onChange={v => setAddingItem(a => ({ ...a, quantity: v === null ? undefined : v }))}
                />
                <Button onClick={handleAddItem}>Ajouter</Button>
              </Space>
              <List
                bordered
                dataSource={items}
                renderItem={(item, idx) => (
                  <List.Item actions={[<Button type="link" onClick={() => handleRemoveItem(idx)}>Supprimer</Button>]}> 
                    {item.productName} x {item.quantity} — {item.totalPrice} F CFA
                  </List.Item>
                )}
              />
            </Space>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loadingForm}>Créer la commande</Button>
          </Form.Item>
        </Form>
      </Card>
    );
  }

  if (loading) return <Spin tip="Chargement de la commande..." />;
  if (error) return <Alert type="error" message={error} />;
  if (!order) return <Alert type="warning" message="Commande introuvable." />;

  const handleUpdateOrder = async (values: any) => {
    try {
      const result = await updateOrder(Number(id), {
        status: values.status,
        shippingAddress: values.shipping_address,
        paymentMethod: values.payment_method,
        notes: values.notes,
        items: editItems.map(item => ({
          id: item.id,
          productId: item.productId || item.product_id,
          quantity: item.quantity,
          unitPrice: item.unitPrice || item.unit_price,
          totalPrice: (item.unitPrice || item.unit_price) * item.quantity
        }))
      } as any);
      
      if (result) {
        setOrder(result);
        setIsEditing(false);
      }
    } catch (e) {
      console.error('Erreur lors de la mise à jour:', e);
    }
  };

  const handleStartEditing = () => {
    // Initialiser editItems avec les items actuels de la commande
    if (order && order.items) {
      setEditItems(order.items.map((item: any) => ({
        id: item.id,
        productId: item.productId || item.product_id,
        productName: item.product?.nom || item.product_name || item.productName,
        quantity: item.quantity,
        unitPrice: item.unitPrice || item.unit_price || item.product?.prix,
        total: item.total || (item.quantity * (item.unitPrice || item.unit_price || item.product?.prix))
      })));
    }
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setEditItems([]);
    setAddingItem({});
    // Réinitialiser le formulaire avec les valeurs actuelles
    if (order) {
      editForm.setFieldsValue({
        status: order.status,
        shipping_address: order.shippingAddress,
        payment_method: order.paymentMethod,
        notes: order.notes
      });
    }
  };

  const handleAddEditItem = () => {
    const prod = products.find((p: any) => p.id === addingItem.productId);
    if (!prod || !addingItem.quantity || addingItem.quantity < 1) return;
    setEditItems([...editItems, {
      productId: prod.id,
      productName: prod.nom,
      quantity: addingItem.quantity,
      unitPrice: prod.prix,
      total: prod.prix * addingItem.quantity
    }]);
    setAddingItem({});
  };

  const handleRemoveEditItem = (idx: number) => {
    setEditItems(editItems.filter((_, i) => i !== idx));
  };

  const handleUpdateItemQuantity = (idx: number, newQuantity: number) => {
    const newItems = [...editItems];
    newItems[idx].quantity = newQuantity;
    newItems[idx].total = newItems[idx].unitPrice * newQuantity;
    setEditItems(newItems);
  };

  const handleDownloadInvoice = async () => {
    if (!order) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        message.error('Veuillez vous connecter pour télécharger la facture');
        return;
      }

      message.loading('Génération de la facture PDF...', 0);

      // Appeler l'API backend pour générer le PDF
      const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:3000/api').replace(/\/api$/, '');
      const response = await fetch(`${apiBase}/api/payments/invoice/${order.id}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la génération du PDF');
      }

      // Récupérer le blob PDF
      const blob = await response.blob();
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `facture-${order.reference || order.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.destroy();
      message.success('Facture PDF téléchargée avec succès');
    } catch (error) {
      message.destroy();
      console.error('Erreur lors du téléchargement de la facture:', error);
      message.error('Erreur lors du téléchargement de la facture');
    }
  };

  const handlePayment = async () => {
    if (!order) return;
    
    // Vérifier le statut de la commande
    if (order.status === 'CANCELLED') {
      message.error('Impossible de payer une commande annulée');
      return;
    }
    
    if (order.paymentStatus === 'PAID') {
      message.info('Cette commande a déjà été payée');
      return;
    }
    
    try {
      message.loading('Traitement du paiement...', 0);
      
      // Utiliser le service au lieu de fetch direct
      const result = await updatePaymentStatus(order.id!, 'paid');
      
      message.destroy();
      
      if (result) {
        message.success('Paiement effectué avec succès !');
        
        // Mettre à jour le statut de paiement localement
        setOrder({
          ...order,
          paymentStatus: 'PAID'
        });
        
        console.log('✅ Statut de paiement mis à jour:', result);
      }
    } catch (error) {
      message.destroy();
      console.error('❌ Erreur lors du paiement:', error);
      message.error(error instanceof Error ? error.message : 'Erreur lors du traitement du paiement');
    }
  };

  const handleChangePaymentStatus = async (newStatus: string) => {
    if (!order) return;
    
    const statusLabels: Record<string, string> = {
      'pending': 'En attente',
      'paid': 'Payée',
      'failed': 'Échouée',
      'refunded': 'Remboursée'
    };
    
    try {
      message.loading(`Mise à jour du statut vers "${statusLabels[newStatus]}"...`, 0);
      
      const result = await updatePaymentStatus(order.id!, newStatus);
      
      message.destroy();
      
      if (result) {
        message.success(`Statut de paiement mis à jour : ${statusLabels[newStatus]}`);
        
        // Mettre à jour le statut de paiement localement
        setOrder({
          ...order,
          paymentStatus: newStatus.toUpperCase() as Order['paymentStatus']
        });
        
        console.log('✅ Statut de paiement mis à jour:', result);
      }
    } catch (error) {
      message.destroy();
      console.error('❌ Erreur lors de la mise à jour du statut:', error);
      message.error(error instanceof Error ? error.message : 'Erreur lors de la mise à jour du statut de paiement');
    }
  };

  return (
    <Card 
      title={
        <Space>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/commandes')}
          />
          <span>Commande #{order.id}</span>
        </Space>
      }
      extra={
        !isEditing ? (
          <Space>
            <Button 
              icon={<DownloadOutlined />} 
              onClick={handleDownloadInvoice}
            >
              Télécharger
            </Button>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'pending',
                    label: 'En attente',
                    onClick: () => handleChangePaymentStatus('pending'),
                    disabled: order.paymentStatus === 'PENDING'
                  },
                  {
                    key: 'paid',
                    label: 'Marquer comme payée',
                    onClick: () => handleChangePaymentStatus('paid'),
                    disabled: order.paymentStatus === 'PAID'
                  },
                  {
                    key: 'failed',
                    label: 'Marquer comme échouée',
                    onClick: () => handleChangePaymentStatus('failed'),
                    disabled: order.paymentStatus === 'FAILED'
                  },
                  {
                    key: 'refunded',
                    label: 'Marquer comme remboursée',
                    onClick: () => handleChangePaymentStatus('refunded'),
                    disabled: order.paymentStatus === 'REFUNDED'
                  }
                ]
              }}
              disabled={order.status === 'CANCELLED'}
            >
              <Button type="default" icon={<CreditCardOutlined />}>
                Statut paiement <DownOutlined />
              </Button>
            </Dropdown>
            <Button type="primary" icon={<EditOutlined />} onClick={handleStartEditing}>
              Modifier
            </Button>
          </Space>
        ) : (
          <Space>
            <Button icon={<CloseOutlined />} onClick={handleCancelEditing}>
              Annuler
            </Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={() => editForm.submit()}>
              Enregistrer
            </Button>
          </Space>
        )
      }
    >
      {!isEditing ? (
        <>
          <Space direction="vertical" style={{ width: '100%' }}>
            {order.reference && <Text><strong>Référence :</strong> {order.reference}</Text>}
            <Text><strong>Statut :</strong> {getStatusLabel(order.status)}</Text>
            <Text><strong>Statut de paiement :</strong> {getPaymentStatusLabel(order.paymentStatus)}</Text>
            <Text><strong>Date de création :</strong> {
              order.createdAt 
                ? new Date(order.createdAt).toLocaleString('fr-FR', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric', 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })
                : (order as any).created_at 
                  ? new Date((order as any).created_at).toLocaleString('fr-FR', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric', 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })
                  : 'Non disponible'
            }</Text>
            <Text><strong>Client :</strong> {(order as any).customer?.first_name} {(order as any).customer?.last_name} ({(order as any).customer?.email || order.customerName || order.customerId})</Text>
            <Text><strong>Adresse de livraison :</strong> {order.shippingAddress || 'Non spécifiée'}</Text>
            <Text><strong>Mode de paiement :</strong> {getPaymentMethodLabel(order.paymentMethod)}</Text>
            <Text><strong>Notes :</strong> {order.notes || 'Aucune'}</Text>
          </Space>
          <Title level={4} style={{ marginTop: 24 }}>Articles</Title>
          <List
            dataSource={order.items || []}
            renderItem={(item: any) => (
              <List.Item>
                <Text>{item.product?.nom || item.product_name || item.productName} x {item.quantity} — {item.total ?? item.unitPrice} CFA</Text>
              </List.Item>
            )}
          />
          <Title level={5} style={{ marginTop: 16 }}>Total : {order.totalAmount} CFA</Title>
          
          {/* Générateur de lien de paiement FineoPay */}
          {order.paymentStatus !== 'PAID' && order.status !== 'CANCELLED' && (
            <div style={{ marginTop: 24 }}>
              <Divider />
              <Title level={4}>💳 Paiement en ligne FineoPay</Title>
              <PaymentLinkGenerator
                orderId={order.id!}
                amount={order.totalAmount}
                reference={order.reference}
                onSuccess={(url) => {
                  console.log('✅ Lien de paiement généré:', url);
                }}
              />
            </div>
          )}
        </>
      ) : (
        <Form form={editForm} layout="vertical" onFinish={handleUpdateOrder}>
          <Form.Item label="Statut" name="status" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="pending">En attente</Select.Option>
              <Select.Option value="processing">En cours</Select.Option>
              <Select.Option value="completed">Terminée</Select.Option>
              <Select.Option value="cancelled">Annulée</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label="Adresse de livraison" name="shipping_address">
            <Input />
          </Form.Item>
          <Form.Item label="Mode de paiement" name="payment_method" rules={[{ required: true, message: 'Veuillez sélectionner un mode de paiement' }]}>
            <Select placeholder="Sélectionner un mode de paiement">
              <Select.Option value="cash">Espèces</Select.Option>
              <Select.Option value="mobile_money">Mobile Money</Select.Option>
              <Select.Option value="bank_transfer">Virement bancaire</Select.Option>
              <Select.Option value="card">Carte bancaire</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label="Notes" name="notes">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Space direction="vertical" style={{ width: '100%', marginTop: 16 }}>
            <Text strong>Client : {(order as any).customer?.first_name} {(order as any).customer?.last_name} ({(order as any).customer?.email})</Text>
            <Text strong>Date de création : {
              order.createdAt 
                ? new Date(order.createdAt).toLocaleString('fr-FR', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric', 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })
                : (order as any).created_at 
                  ? new Date((order as any).created_at).toLocaleString('fr-FR', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric', 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })
                  : 'Non disponible'
            }</Text>
          </Space>
          
          <Title level={4} style={{ marginTop: 24 }}>Articles</Title>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
              <Select
                style={{ minWidth: 200 }}
                placeholder="Ajouter un produit"
                value={addingItem.productId}
                onChange={v => setAddingItem(a => ({ ...a, productId: v }))}
              >
                {products.map((p: any) => (
                  <Select.Option key={p.id} value={p.id}>{p.nom} - {p.prix} CFA</Select.Option>
                ))}
              </Select>
              <InputNumber
                min={1}
                placeholder="Quantité"
                value={addingItem.quantity}
                onChange={v => setAddingItem(a => ({ ...a, quantity: v === null ? undefined : v }))}
              />
              <Button onClick={handleAddEditItem}>Ajouter</Button>
            </Space>
            <List
              bordered
              dataSource={editItems}
              renderItem={(item, idx) => (
                <List.Item 
                  actions={[
                    <InputNumber 
                      key="qty"
                      min={1} 
                      value={item.quantity} 
                      onChange={(v) => handleUpdateItemQuantity(idx, v || 1)}
                      size="small"
                    />,
                    <Button key="del" type="link" danger onClick={() => handleRemoveEditItem(idx)}>Supprimer</Button>
                  ]}
                >
                  <Text>{item.productName} — {item.unitPrice} CFA × {item.quantity} = {item.total} CFA</Text>
                </List.Item>
              )}
            />
          </Space>
          <Title level={5} style={{ marginTop: 16 }}>
            Total : {editItems.reduce((sum, item) => sum + item.total, 0)} CFA
          </Title>
        </Form>
      )}
    </Card>
  );
};

export default OrderDetail;
